import {
  ActionIcon,
  Button,
  Group,
  Input,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChipButton } from '@/components/ChipButton';
import { ConfirmActionButton } from '@/components/ConfirmActionButton';
import type { RouteFormValues } from '@/services';
import type { ExtraDurationHours, RouteDefinition } from '@/types';
import { todayIsoDate } from '@/utils';

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const EXTRA_DURATIONS: readonly ExtraDurationHours[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const fieldLabelProps = {
  style: { letterSpacing: '0.08em', fontSize: '0.6875rem', fontWeight: 700 },
};

interface RouteFormModalProps {
  opened: boolean;
  onClose: () => void;
  /** When set, the modal edits this route; otherwise it creates a new one. */
  route: RouteDefinition | null;
  onSubmit: (values: RouteFormValues) => Promise<void>;
  existingExtraDurations?: readonly ExtraDurationHours[];
  /** Only provided in edit mode; omitting it hides the delete action entirely. */
  onDelete?: () => Promise<void>;
}

const buildEmptyValues = (): RouteFormValues => ({
  category: 'pakkeshop',
  extraDurationHours: null,
  name: '',
  distanceKm: 0,
  payAmount: 0,
  startTime: '08:00',
  endTime: '10:00',
  active: true,
  activeFrom: todayIsoDate(),
  activeUntil: null,
});

export const RouteFormModal = ({
  opened,
  onClose,
  route,
  onSubmit,
  existingExtraDurations = [],
  onDelete,
}: RouteFormModalProps) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<RouteFormValues>({
    initialValues: buildEmptyValues(),
    validate: {
      name: (value, values) =>
        values.category === 'extra' || value.trim().length > 0
          ? null
          : t('modals.routeForm.errors.nameRequired'),
      distanceKm: (value, values) =>
        values.category === 'extra' || value > 0
          ? null
          : t('modals.routeForm.errors.mustBePositive'),
      payAmount: (value, values) =>
        values.category === 'extra' || value > 0
          ? null
          : t('modals.routeForm.errors.mustBePositive'),
      startTime: (value, values) =>
        values.category === 'extra' || TIME_PATTERN.test(value)
          ? null
          : t('modals.routeForm.errors.timeFormat'),
      endTime: (value, values) =>
        values.category === 'extra' || TIME_PATTERN.test(value)
          ? null
          : t('modals.routeForm.errors.timeFormat'),
      extraDurationHours: (value, values) => {
        if (values.category !== 'extra') return null;
        if (value === null || !EXTRA_DURATIONS.includes(value)) {
          return t('modals.routeForm.errors.durationRequired');
        }
        return existingExtraDurations.includes(value)
          ? t('modals.routeForm.errors.duplicateExtra')
          : null;
      },
      activeFrom: (value, values) => {
        if (values.category === 'extra') return null;
        if (!value) return t('modals.routeForm.errors.startDateRequired');
        // Existing routes may already have a past activeFrom; only block it
        // from being (re)set to a past date, not from being left unchanged.
        if (value !== route?.activeFrom && value < todayIsoDate()) {
          return t('modals.routeForm.errors.notBeforeToday');
        }
        return null;
      },
      activeUntil: (value, values) => {
        if (values.category === 'extra') return null;
        if (!value) return null;
        if (value !== route?.activeUntil && value < todayIsoDate()) {
          return t('modals.routeForm.errors.notBeforeToday');
        }
        if (value < values.activeFrom) return t('modals.routeForm.errors.notBeforeStartDate');
        return null;
      },
    },
  });

  // Reset the form each time the modal opens, seeding it from `route` if editing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when the modal opens or the target route changes, not on every form state change.
  useEffect(() => {
    if (opened) {
      form.setValues(
        route
          ? {
              ...route,
              activeFrom: route.activeFrom ?? todayIsoDate(),
              activeUntil: route.activeUntil ?? null,
            }
          : buildEmptyValues(),
      );
    }
  }, [opened, route]);

  const adjustExtraDuration = (change: -1 | 1) => {
    const currentDuration = form.values.extraDurationHours ?? 0;
    const nextDuration = EXTRA_DURATIONS.find((duration) => duration === currentDuration + change);
    if (nextDuration !== undefined) {
      form.setFieldValue('extraDurationHours', nextDuration);
    }
  };

  const handleSubmit = async (values: RouteFormValues) => {
    setSubmitting(true);
    try {
      await onSubmit(values);
      onClose();
    } catch {
      notifications.show({
        color: 'red',
        message: t('modals.routeForm.errors.saveFailed'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    try {
      await onDelete();
      onClose();
    } catch {
      notifications.show({
        color: 'red',
        message: t('modals.routeForm.errors.deleteFailed'),
      });
    }
  };

  return (
    <Modal
      fullScreen
      onClose={onClose}
      opened={opened}
      title={
        <Text c="signal.7" ff="monospace" fw={700} size="xs" style={{ letterSpacing: '0.2em' }}>
          {route ? t('modals.routeForm.editTitle') : t('modals.routeForm.newTitle')}
        </Text>
      }
      transitionProps={{ transition: 'slide-up' }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md" maw={480} mx="auto" pt="sm">
          <Select
            data={[
              { value: 'pakkeshop', label: t('routeCategories.pakkeshop') },
              { value: 'collection', label: t('routeCategories.collection') },
              { value: 'extra', label: t('routeCategories.extra') },
            ]}
            label={t('modals.routeForm.category')}
            labelProps={fieldLabelProps}
            onChange={(value) => {
              if (value === 'pakkeshop' || value === 'collection' || value === 'extra') {
                form.setFieldValue('category', value);
                if (value !== 'extra') {
                  form.setFieldValue('extraDurationHours', null);
                  if (form.values.category === 'extra') {
                    form.setFieldValue('activeFrom', todayIsoDate());
                    form.setFieldValue('activeUntil', null);
                  }
                } else if (form.values.extraDurationHours === null) {
                  form.setFieldValue('extraDurationHours', 1);
                }
              }
            }}
            value={form.values.category}
          />
          {form.values.category === 'extra' ? (
            <Input.Wrapper
              error={form.errors.extraDurationHours}
              label={t('modals.routeForm.duration')}
              labelElement="div"
              labelProps={fieldLabelProps}
            >
              <Group align="center" gap="sm" mt={4} wrap="nowrap">
                <ActionIcon
                  aria-label={t('modals.routeForm.decreaseDuration')}
                  color="signal.7"
                  disabled={!form.values.extraDurationHours || form.values.extraDurationHours <= 1}
                  onClick={() => adjustExtraDuration(-1)}
                  size={52}
                  type="button"
                  variant="outline"
                >
                  <IconMinus size={30} stroke={2.25} />
                </ActionIcon>
                <NumberInput
                  allowDecimal={false}
                  aria-label={t('modals.routeForm.duration')}
                  clampBehavior="strict"
                  hideControls
                  max={12}
                  min={1}
                  onChange={(value) => {
                    const hours = typeof value === 'number' ? value : Number(value);
                    const duration =
                      EXTRA_DURATIONS.find((candidate) => candidate === hours) ?? null;
                    form.setFieldValue('extraDurationHours', duration);
                  }}
                  size="lg"
                  step={1}
                  styles={{ input: { textAlign: 'center' } }}
                  value={form.values.extraDurationHours ?? ''}
                  w={80}
                />
                <ActionIcon
                  aria-label={t('modals.routeForm.increaseDuration')}
                  color="signal.7"
                  disabled={form.values.extraDurationHours === 12}
                  onClick={() => adjustExtraDuration(1)}
                  size={52}
                  type="button"
                  variant="outline"
                >
                  <IconPlus size={30} stroke={2.25} />
                </ActionIcon>
              </Group>
            </Input.Wrapper>
          ) : (
            <>
              <TextInput
                label={t('modals.routeForm.name')}
                labelProps={fieldLabelProps}
                placeholder={t('modals.routeForm.namePlaceholder')}
                {...form.getInputProps('name')}
              />
              <NumberInput
                decimalScale={1}
                label={t('modals.routeForm.distance')}
                labelProps={fieldLabelProps}
                min={0}
                {...form.getInputProps('distanceKm')}
              />
              <NumberInput
                decimalScale={2}
                label={t('modals.routeForm.payAmount')}
                labelProps={fieldLabelProps}
                min={0}
                {...form.getInputProps('payAmount')}
              />
              <Group grow>
                <TextInput
                  label={t('modals.routeForm.startTime')}
                  labelProps={fieldLabelProps}
                  placeholder="08:00"
                  {...form.getInputProps('startTime')}
                />
                <TextInput
                  label={t('modals.routeForm.endTime')}
                  labelProps={fieldLabelProps}
                  placeholder="10:00"
                  {...form.getInputProps('endTime')}
                />
              </Group>
            </>
          )}
          {form.values.category !== 'extra' && (
            <Group grow>
              <DateInput
                clearable={false}
                label={t('modals.routeForm.activeFrom')}
                labelProps={fieldLabelProps}
                minDate={todayIsoDate()}
                valueFormat="DD/MM/YYYY"
                {...form.getInputProps('activeFrom')}
              />
              <DateInput
                clearable
                label={t('modals.routeForm.activeUntil')}
                labelProps={fieldLabelProps}
                minDate={form.values.activeFrom || todayIsoDate()}
                placeholder={t('modals.routeForm.activeUntilPlaceholder')}
                valueFormat="DD/MM/YYYY"
                {...form.getInputProps('activeUntil')}
              />
            </Group>
          )}
          <Switch
            color="signal"
            label={t('modals.routeForm.active')}
            {...form.getInputProps('active', { type: 'checkbox' })}
          />
          <Group grow mt="lg">
            <ChipButton
              active={false}
              fullWidth
              label={t('modals.routeForm.cancel')}
              onClick={onClose}
            />
            <Button
              loading={submitting}
              style={{ boxShadow: 'none', fontWeight: 700 }}
              type="submit"
            >
              {route ? t('modals.routeForm.saveChanges') : t('modals.routeForm.createRoute')}
            </Button>
          </Group>
          {route && onDelete && (
            <ConfirmActionButton
              confirmLabel={t('modals.routeForm.confirmDeleteRoute')}
              fullWidth
              label={t('modals.routeForm.deleteRoute')}
              noShadow
              onConfirm={handleDelete}
            />
          )}
        </Stack>
      </form>
    </Modal>
  );
};
